import { Response } from 'axios';
import * as _ from 'lodash';
import * as React from 'react';
import {
    AchievementConfig,
    AchievementsConstraints,
    AchievementTemplate,
    BonusConfig
} from '../../../../generated/protocol';
import { kernel } from '../../../ioc/IOCHelper';
import { Symbols } from '../../../ioc/Symbols';
import { AchievementsService } from '../../../service/achievements/AchievementsService';
import { Condition } from '../../form/Condition';
import { Form, FormListener } from '../../form/Form';
import { FormSubmitError, FormSubmitSuccess } from '../../form/FormFeedback';
import { FormGroup } from '../../form/FormGroup';
import { FormInput, FormJavascript } from '../../form/FormInput';
import { FormTextArea } from '../../form/FormTextArea';
import { SelectInput } from '../../form/SelectInput';
import { Submit } from '../../form/Submit';
import { Case, Switch } from '../../form/Switch';
import { Validators } from '../../form/Validators';
import { ButtonGroup } from '../../navbar/ButtonGroup';
import { DeleteButtonModal } from '../../util/DeleteButtonModal';
import { RefreshButton } from '../../util/RefreshButton';
import { RequiresAdminRole } from '../../util/RequiresAdminRole';
import { LoadingViewState, RouterView } from '../RouterView';

const EXPRESSION: string = 'Expression';
const SCRIPT: string = 'Script';
const ATTRIBUTE: string = 'Attribute';

interface State extends LoadingViewState {
    achievement?: AchievementConfig;
    constraints?: AchievementsConstraints;
    ruleState?: string;
    valueState?: string;
    refreshingConstraints?: boolean;
    validationMap?: any;
}

export class AchievementTemplateType {
    static counter: AchievementTemplate = 'counter';
    static accumulator: AchievementTemplate = 'accumulator';
    static item_award: AchievementTemplate = 'item_award';
    static bonus_activation: AchievementTemplate = 'bonus_activation';

    static templateEqual(template: string, templateType: AchievementTemplateType): boolean {
        return template === templateType;
    }

    static templateIsNotOneOf(template: string, ...templateTypeArray: AchievementTemplateType[]): boolean {
        const templateIncluded = _.includes(templateTypeArray, template)
        return !templateIncluded;
    }
}

export class TriggersConfigView
    extends RouterView<{ params: { id: string } }, State> implements FormListener<AchievementConfig> {

    achievementService: AchievementsService;

    constructor() {
        super();
        this.state = {
            achievement: {},
            constraints: {},
            loading: true,
            ruleState: EXPRESSION,
            validationMap: {},
            valueState: ATTRIBUTE,
        };
        this.achievementService = kernel.get<AchievementsService>(Symbols.AchievementsService);
    }

    loadContent(): Promise<any> {
        return this.init();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.params.id !== this.props.params.id) {
            this.init();
        }
    }

    refreshConstraints() {
        if (this.state.refreshingConstraints === true) {
            return;
        }
        this.setState({refreshingConstraints: true});
        this.achievementService.getConstraints().then(constraints => {
            this.setState({constraints, refreshingConstraints: false});
        }).catch(err => {
            this.setState({refreshingConstraints: false});
        });
    }

    init(): Promise<any> {
        if (this.props.params.id === 'new') {
            return this.load(this.achievementService.getConstraints()).then(constraints => {
                this.setState({
                    achievement: {template: AchievementTemplateType.accumulator as AchievementTemplate},
                    constraints,
                    loading: false,
                });
            });
        } else {
            return this.loadAll(
                this.achievementService.getEntity(this.props.params.id),
                this.achievementService.getConstraints(),
            ).then(results => {
                const ruleScript = (results[0] as any).ruleScript;
                const valueScript = (results[0] as any).valueScript;
                const ruleExpression = ruleScript == undefined
                    || typeof ruleScript === 'undefined' || ruleScript === '';
                const valueAttribute = valueScript == undefined
                    || typeof valueScript === 'undefined' || valueScript === '';
                this.setState({
                    achievement: results[0],
                    constraints: results[1],
                    loading: false,
                    ruleState: ruleExpression ? EXPRESSION : SCRIPT,
                    valueState: valueAttribute ? ATTRIBUTE : SCRIPT,
                });
            });
        }
    }

    onSubmit(): Promise<any> {
        return this.achievementService.save(this.state.achievement).then(achievement => {
            if (this.props.params.id === 'new') {
                this.navigate(`/trigger/${achievement.id}`);
            } else {
                this.setState({achievement});
            }
        }).catch(err => {
            throw err.message;
        });
    }

    formDidUpdate(formModel: AchievementConfig, validationMap: any) {
        if (AchievementTemplateType.templateEqual(formModel.template, AchievementTemplateType.counter)) {
            formModel.attribute = 'rake';
        }
        const templateChange = this.state.achievement.template !== formModel.template;
        if (AchievementTemplateType.templateEqual(formModel.template, AchievementTemplateType.item_award)
            && templateChange) {
            formModel = {...formModel, itemAward: {}};
        } else if (AchievementTemplateType.templateEqual(formModel.template, AchievementTemplateType.bonus_activation)
            && templateChange) {
            formModel = {...formModel, bonusActivation: {}};
        } else if
            ((AchievementTemplateType.templateIsNotOneOf(formModel.template,
                AchievementTemplateType.bonus_activation, AchievementTemplateType.item_award))
            && templateChange) {
            formModel = {...formModel, bonusActivation: null, itemAward: null};
        }

        if (formModel.eventType === 'GAME' && formModel.eventType !== this.state.achievement.eventType) {
            formModel = {...formModel, game: 'poker'};
        } else {
            formModel = {...formModel, game: null};
        }
        this.setState({achievement: formModel, validationMap});
    }

    getAttributeOptions() {
        if (this.state.achievement.eventType === 'PLAYER') {
            return this.state.constraints.playerAttributes.map(a => {
                return <option key={a} value={a}>{a}</option>;
            });
        } else {
            return this.state.constraints.pokerAttributes.map(a => {
                return <option key={a} value={a}>{a}</option>;
            });
        }

    }

    getAttributeOptionsDescription() {
        if (this.state.achievement.eventType === 'PLAYER') {
            return this.state.constraints.playerAttributes.join(', ');
        } else {
            return this.state.constraints.pokerAttributes.join(', ');
        }
    }

    deleteLeaderboard() {
        return this.achievementService.remove(this.state.achievement.id).then(() => {
            this.navigate('/achievements');
        }).catch((err: Response) => {
            throw new Error(`Unable to delete achievement ${err.statusText}`);
        });
    }

    getRuleInput() {
        if (this.state.ruleState === EXPRESSION) {
            return (
                <FormGroup className='form-group' model='ruleExpression'>
                    <label className='col-sm-2 control-label'>Rule Expression</label>
                    <div className='col-sm-10'>
                        <FormInput type='text' model='ruleExpression'
                                   validators={[Validators.ruleExpression(), Validators.required()]}
                                   className='form-control'/>
                        <span className='help-block m-b-none'>
                          Use this to filter out events from this achievement.
                      </span>
                    </div>
                </FormGroup>
            );

        } else {
            return (
                <FormGroup className='form-group' model='ruleScript'>
                    <label className='col-sm-2 control-label'>Rule Script</label>
                    <div className='col-sm-10'>

                        <FormJavascript model='ruleScript'/>
                        <span className='help-block m-b-none'>
                            Use this to filter out events from this achievement.
                        </span>
                    </div>
                </FormGroup>
            );
        }
    }

    toggleRule(state: string) {
        const achievement = _.clone(this.state.achievement);
        if (state === EXPRESSION) {
            achievement.ruleScript = null;
            this.setState({ruleState: EXPRESSION, achievement});
        } else {
            achievement.ruleExpression = null;
            achievement.ruleScript = 'function evaluate(attributes){\n   return true; \n}';
            this.setState({ruleState: SCRIPT, achievement});
        }
    }

    toggleValue(state: string) {
        const achievement = _.clone(this.state.achievement);
        if (state === ATTRIBUTE) {
            achievement.valueScript = null;
            this.setState({valueState: ATTRIBUTE, achievement});
        } else {
            achievement.attribute = null;
            achievement.valueScript = 'function evaluate(attributes){\n   return 1.0; \n}';
            this.setState({valueState: SCRIPT, achievement});
        }
    }

    getAttributeOrScriptToggle() {
        if (AchievementTemplateType.templateIsNotOneOf
            (this.state.achievement.template, AchievementTemplateType.accumulator)) {
            return <div/>;
        }
        return (
            <div className='col-sm-10 col-sm-offset-2'>
                <ButtonGroup actions={[ATTRIBUTE, SCRIPT]} active={this.state.valueState}
                             onChange={this.toggleValue.bind(this)}/>
            </div>
        );
    }

    currentTypes() {
        if (this.state.achievement.eventType === 'PLAYER') {
            return this.state.constraints.playerTypes;
        }
        return this.state.constraints.pokerTypes;
    }

    getValueAttributeOrScript() {
        if (AchievementTemplateType.templateIsNotOneOf
            (this.state.achievement.template, AchievementTemplateType.accumulator)) {
            return <div/>;
        }

        return (
            <Switch value={this.state.valueState}>
                <Case value={ATTRIBUTE}>
                    <FormGroup className='form-group' model='attribute'>
                        <label className='col-sm-2 control-label'>Value to Accumulate</label>
                        <div className='col-sm-10'>
                            <SelectInput model='attribute' className='form-control'>
                                {this.getAttributeOptions()}
                            </SelectInput>
                            <span className='help-block m-b-none'>
                                The event attribute to accumulate/count, leave empty if using value script
                            </span>
                        </div>
                    </FormGroup>
                    <div className='hr-line-dashed'></div>
                </Case>
                <Case value={SCRIPT}>
                    <FormGroup className='form-group' model='valueScript'>
                        <label className='col-sm-2 control-label'>Value Script</label>
                        <div className='col-sm-10'>

                            <FormJavascript model='valueScript'/>
                            <span className='help-block m-b-none'>
                                Use this to filter out events from this achievement.
                            </span>
                        </div>
                    </FormGroup>
                    <div className='hr-line-dashed'></div>
                </Case>
            </Switch>
        );
    }

    getTopButtons() {
        if (this.props.params.id !== 'new') {
            return (
                <div className='page-top-actions'>
                    <DeleteButtonModal className='btn-lg btn-danger' onDelete={this.deleteLeaderboard.bind(this)}
                                       description={this.state.achievement.name}/>
                    <a onClick={() => {
                        this.navigate(`/trigger/show/${this.state.achievement.nameId}`);
                    }} className='btn-lg btn-primary '>Show</a>
                </div>
            );
        } else {
            return <div/>;
        }
    }

    getPreviewImage() {
        return (
            <div className='form-group'>
                <label className='col-sm-2 control-label'>Preview</label>
                <div className='col-sm-10'>
                    <img src={this.state.achievement.image} style={{width: '100px', height: '100px'}}/>
                </div>
            </div>
        );
    }

    getIntervalConfig(): JSX.Element {
        const tmpl = this.state.achievement.template;
        if (AchievementTemplateType.templateIsNotOneOf(tmpl,
            AchievementTemplateType.counter, AchievementTemplateType.accumulator)) {
            return undefined;
        }
        return (
            <div className='row'>
                <div className='col-lg-12'>
                    <div className='ibox float-e-margins'>
                        <div className='ibox-title'>
                            <h5>Time Reset Settings</h5>
                        </div>
                        <div className='ibox-content wizard'>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group'>
                                <label className='col-sm-2 control-label'>Interval</label>
                                <div className='col-sm-10'>
                                    <SelectInput model='interval' values={this.state.constraints.intervals}
                                                 sort={false}
                                                 className='form-control'/>
                                    <span className='help-block m-b-none'>
                                        How often the trigger should reset
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group' model='intervalOffset'>
                                <label className='col-sm-2 control-label'>Interval Offset</label>
                                <div className='col-sm-10'>
                                    <FormInput type='integer' model='intervalOffset' className='form-control'
                                               validators={[Validators.integer()]}/>
                                    <span className='help-block m-b-none'>
                                    Hours to offset the reset interval,
                                    if 24 and WEEK interval it will reset on Tuesday 00:00 UTC
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                        </div>
                    </div>
                </div>
            </div>
        );
    }

    getPresentationConfig(): JSX.Element {

        return (
            <div className='row'>
                <div className='col-lg-12'>
                    <div className='ibox float-e-margins'>
                        <div className='ibox-title'>
                            <h5>Presentation Settings</h5>
                        </div>
                        <div className='ibox-content wizard'>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group'>
                                <label className='col-sm-2 control-label'>Category</label>
                                <div className='col-sm-10'>
                                    <SelectInput model='category' values={this.state.constraints.categories}
                                                 className='form-control'/>
                                    <span className='help-block m-b-none'>
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group'>
                                <label className='col-sm-2 control-label'>Visibility</label>
                                <div className='col-sm-10'>
                                    <SelectInput model='visibility' values={this.state.constraints.visibilities}
                                                 className='form-control'/>
                                    <span className='help-block m-b-none'>
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group'>
                                <label className='col-sm-2 control-label'>Progress Type</label>
                                <div className='col-sm-10'>
                                    <SelectInput model='progressType' values={this.state.constraints.progressTypes}
                                                 className='form-control'/>
                                    <span className='help-block m-b-none'>
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group'>
                                <label className='col-sm-2 control-label'>Notifications</label>
                                <div className='col-sm-10'>
                                    <SelectInput model='notification' values={this.state.constraints.notifications}
                                                 className='form-control'/>
                                    <span className='help-block m-b-none'>
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group' model='image'>
                                <label className='col-sm-2 control-label'>Image URL</label>
                                <div className='col-sm-10'>
                                    <FormInput type='text' model='image' className='form-control'/>
                                    <span className='help-block m-b-none'>
                                        URL to the image to display in the client
                                    </span>
                                </div>
                            </FormGroup>
                            {this.getPreviewImage()}

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group' model='rank'>
                                <label className='col-sm-2 control-label'>Rank</label>
                                <div className='col-sm-10'>
                                    <FormInput type='text' model='rank' className='form-control'
                                               validators={[Validators.number()]}/>
                                    <span className='help-block m-b-none'>
                                        The trigger rank
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group' model='value'>
                                <label className='col-sm-2 control-label'>Value</label>
                                <div className='col-sm-10'>
                                    <FormInput type='text' model='value' className='form-control'
                                               validators={[Validators.number()]}/>
                                    <span className='help-block m-b-none'>
                                        The trigger value
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group' model='order'>
                                <label className='col-sm-2 control-label'>Order</label>
                                <div className='col-sm-10'>
                                    <FormInput type='text' model='order' className='form-control'
                                               validators={[Validators.number()]}/>
                                    <span className='help-block m-b-none'>
                                        The order of the trigger
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                            <FormGroup className='form-group' model='rewardDescription'>
                                <label className='col-sm-2 control-label'>Reward Description</label>
                                <div className='col-sm-10'>
                                    <FormInput type='text' model='rewardDescription' className='form-control'/>
                                    <span className='help-block m-b-none'>
                                        Description displayed when the trigger is awarded
                                    </span>
                                </div>
                            </FormGroup>

                            <div className='hr-line-dashed'></div>

                        </div>
                    </div>
                </div>
            </div>
        );
    }

    getBonusDisplayValue(item: BonusConfig) {
        return `${item.nameId} - ${item.name ? item.name.substr(0, Math.min(30, item.name.length - 1)) : ''}`;
    }

    isDynamicAmountBonus(id): boolean {
        return this.state.constraints.bonuses.some(b => {
            return b.id === id && b.type === 'DYNAMIC_AMOUNT';
        });
    }

    getBonusActivationAttribute(): JSX.Element {
        const bonusActivation = this.state.achievement.bonusActivation;
        if (bonusActivation == undefined || !this.isDynamicAmountBonus(bonusActivation.bonusId)) {
            return undefined;
        }
        const typeFilter = this.state.achievement.typeFilter;
        return (
            <FormGroup className='form-group' model=''>
                <label className='col-sm-2 control-label'>Activation amount:</label>
                <div className='col-sm-10'>
                    <FormInput className='form-control' validators={[Validators.required()]} type='text'
                               model='bonusActivation.bonusActivationAttribute'/>
                    <span className='help-block m-b-none'>
                    An activation amount or one of the following event attributes:
                        {this.getAttributeOptionsDescription()}
                    </span>
                </div>
            </FormGroup>
        );
    }

    toggleBonusActivation() {
        let update;
        if (this.state.achievement.bonusActivation == undefined) {
            update = {...this.state.achievement, bonusActivation: {}};
        } else {
            update = {...this.state.achievement, bonusActivation: null};
        }
        this.setState({achievement: update});
    }

    toggleItemAward() {
        if (this.state.achievement.itemAward == undefined) {
            this.setState({achievement: {...this.state.achievement, itemAward: {}}});
        } else {
            this.setState({achievement: {...this.state.achievement, itemAward: null}});
        }
    }

    getBonusActivation(): JSX.Element {
        const tmpl = this.state.achievement.template;
        if (AchievementTemplateType.templateIsNotOneOf(tmpl, AchievementTemplateType.counter,
            AchievementTemplateType.accumulator, AchievementTemplateType.bonus_activation)) {
            return undefined;
        }

        return (
            <div className='row'>
                <div className='col-lg-12'>
                    <div className='ibox float-e-margins'>
                        <div className='ibox-title'>
                            <h5>Bonus Activation Settings</h5>
                            <RefreshButton onClick={this.refreshConstraints.bind(this)}
                                           refreshing={this.state.refreshingConstraints}/>

                        </div>
                        <div className='ibox-content wizard'>
                            <Condition show={
                                AchievementTemplateType.templateIsNotOneOf
                                (this.state.achievement.template , AchievementTemplateType.bonus_activation)}>
                                <div className='hr-line-dashed'></div>

                                <div className='form-group'>
                                    <label className='col-sm-2 control-label'>Enable Bonus Activation</label>
                                    <div className='col-sm-10'>
                                        <input type='checkbox'
                                               checked={this.state.achievement.bonusActivation != undefined}
                                               onChange={this.toggleBonusActivation.bind(this)}/>
                                    </div>
                                </div>
                            </Condition>

                            <div className='hr-line-dashed'></div>

                            <Condition show={this.state.achievement.bonusActivation != undefined}>
                                <FormGroup className='form-group' model='bonusActivationNameId'>
                                    <label className='col-sm-2 control-label'>Award bonus</label>
                                    <div className='col-sm-10'>
                                        <SelectInput className='form-control' values={this.state.constraints.bonuses}
                                                     model='bonusActivation.bonusId'
                                                     displayValue={this.getBonusDisplayValue}
                                                     optionModelValue={val => {
                                                         return val.id;
                                                     }} validators={[Validators.required()]}/>
                                        <span className='help-block m-b-none'>
                                        The bonus configuration to use when trigger is completed.
                                        Can be set to a bonus that pays out the full amount as withdrawable money.
                                        </span>
                                    </div>
                                </FormGroup>

                                <div className='hr-line-dashed'></div>
                                {this.getBonusActivationAttribute()}

                            </Condition>

                        </div>
                    </div>
                </div>
            </div>
        );
    }

    getItemAward(): JSX.Element {
        const tmpl = this.state.achievement.template;
        if (AchievementTemplateType.templateIsNotOneOf(tmpl,
            AchievementTemplateType.item_award, AchievementTemplateType.counter,
            AchievementTemplateType.accumulator, AchievementTemplateType.bonus_activation)) {
            return undefined;
        }

        return (
            <div className='row'>
                <div className='col-lg-12'>
                    <div className='ibox float-e-margins'>

                        <div className='ibox-title'>
                            <h5>Item Settings</h5>
                            <RefreshButton onClick={this.refreshConstraints.bind(this)}
                                           refreshing={this.state.refreshingConstraints}/>
                        </div>

                        <div className='ibox-content wizard'>

                            <div className='hr-line-dashed'></div>

                            <div className='form-group'>
                                <label className='col-sm-2 control-label'>Enable Item Award</label>
                                <div className='col-sm-10'>
                                    <input type='checkbox' checked={this.state.achievement.itemAward != undefined}
                                           onChange={this.toggleItemAward.bind(this)}/>
                                </div>
                            </div>

                            <Condition show={this.state.achievement.itemAward != undefined}>
                                <FormGroup className='form-group' model='temAward.itemId'>
                                    <label className='col-sm-2 control-label'>Award Item</label>
                                    <div className='col-sm-10'>
                                        <SelectInput className='form-control' values={this.state.constraints.items}
                                                     model='itemAward.itemId'
                                                     displayValue={this.getBonusDisplayValue}
                                                     optionModelValue={val => {
                                                         return val.id;
                                                     }} validators={[Validators.required()]}/>

                                    </div>
                                </FormGroup>

                                <div className='hr-line-dashed'></div>
                                <FormGroup className='form-group' model='temAward.expirationTime'>
                                    <label className='col-sm-2 control-label'>Expiration time:</label>
                                    <div className='col-sm-10'>
                                        <FormInput type='text' className='form-control' model='itemAward.expirationTime'
                                                   validators={[Validators.number()]}/>
                                        <span className='help-block m-b-none'>
                                           When the item will expire and not be visible
                                        </span>
                                    </div>
                                </FormGroup>

                            </Condition>

                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderContent() {

        const constraints = this.state.constraints;
        return (
            <div>
                <div className='row wrapper border-bottom white-bg page-heading'>
                    <div className='col-lg-12'>
                        <h1>Trigger</h1>
                    </div>
                </div>
                <div className='wrapper wrapper-content animated'>
                    <Form className='form-horizontal' formListener={this}
                          modelObject={this.state.achievement}
                          validationMap={this.state.validationMap}>

                        {this.getTopButtons()}

                        <div className='row'>
                            <div className='col-lg-12'>
                                <div className='ibox float-e-margins'>
                                    <div className='ibox-title'>
                                        <h5>Trigger Info</h5>
                                    </div>
                                    <div className='ibox-content'>
                                        <div className='hr-line-dashed'></div>

                                        <FormGroup className='form-group' model='nameId'>
                                            <label className='col-sm-2 control-label'>Name ID</label>
                                            <div className='col-sm-10'>
                                                <FormInput model='nameId'
                                                           disabled={this.state.achievement.id != undefined}
                                                           type='text'
                                                           validators={[Validators.required(), Validators.textId()]}
                                                           className='form-control'/>
                                                <span className='help-block m-b-none'>
                                                    Unique id for a trigger, example: cubiea_top_winners_xcc
                                                </span>
                                            </div>
                                        </FormGroup>

                                        <div className='hr-line-dashed'></div>

                                        <RequiresAdminRole>
                                            <FormGroup className='form-group' model='createdByOperatorId'>
                                                <label className='col-sm-2 control-label'>Operator ID</label>
                                                <div className='col-sm-10'>
                                                    <FormInput type='integer' model='createdByOperatorId'
                                                               validators={[Validators.integer()]}
                                                               className='form-control'/>
                                                    <span className='help-block m-b-none'>
                                                        The id of the operator that owns this trigger (can edit)
                                                    </span>
                                                </div>
                                            </FormGroup>
                                            <div className='hr-line-dashed'></div>
                                        </RequiresAdminRole>

                                        <FormGroup className='form-group' model='name'>
                                            <label className='col-sm-2 control-label'>Name</label>
                                            <div className='col-sm-10'>
                                                <FormInput type='text' model='name'
                                                           validators={[Validators.required(), Validators.textInput()]}
                                                           className='form-control'/>
                                                <span className='help-block m-b-none'>
                                                    The name displayed to players
                                                </span>
                                            </div>
                                        </FormGroup>

                                        <div className='hr-line-dashed'></div>

                                        <FormGroup className='form-group' model='description'>
                                            <label className='col-sm-2 control-label'>Description</label>
                                            <div className='col-sm-10'>
                                                <FormTextArea type='text' model='description'
                                                              validators={[Validators.textInput()]}
                                                              className='form-control'/>
                                            </div>
                                        </FormGroup>

                                        <div className='hr-line-dashed'></div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='row'>
                            <div className='col-lg-12'>
                                <div className='ibox float-e-margins'>
                                    <div className='ibox-title'>
                                        <h5>Counting/Accumulation</h5>
                                    </div>
                                    <div className='ibox-content wizard'>
                                        <div className='hr-line-dashed'></div>

                                        <FormGroup className='form-group'>
                                            <label className='col-sm-2 control-label'>Event type</label>
                                            <div className='col-sm-10'>
                                                <SelectInput model='eventType'
                                                             values={this.state.constraints.eventTypes}
                                                             className='form-control'/>
                                                <span className='help-block m-b-none'>
                                                    Type of event to trigger on
                                                </span>
                                            </div>
                                        </FormGroup>

                                        <FormGroup className='form-group'>
                                            <label className='col-sm-2 control-label'>Trigger type</label>
                                            <div className='col-sm-10'>
                                                <SelectInput model='template' values={this.state.constraints.templates}
                                                             className='form-control'/>
                                                <span className='help-block m-b-none'>
                                                    <Switch value={this.state.achievement.template}>
                                                        <Case value='bonus_activation'>
                                                        Award a bonus EACH TIME the rules expression/script
                                                        match an event
                                                        </Case>
                                                        <Case value='counter'>
                                                        Count the number of events that matches
                                                        the rule expression/script
                                                        </Case>
                                                        <Case value='accumulator'>
                                                        Accumulate an event attribute for each event
                                                        that matches the rule expression/script.
                                                        </Case>
                                                    </Switch>

                                                </span>
                                            </div>
                                        </FormGroup>

                                        <div className='hr-line-dashed'></div>

                                        {this.getAttributeOrScriptToggle()}
                                        {this.getValueAttributeOrScript()}

                                        <FormGroup className='form-group' model='typeFilter'>
                                            <label
                                                className='col-sm-2 control-label'>
                                                {this.state.achievement.eventType === 'GAME' ? 'Game' : 'Player'} Event
                                                Type</label>
                                            <div className='col-sm-10'>
                                                <SelectInput className='form-control type-filter-select'
                                                             validators={[Validators.required()]}
                                                             values={this.currentTypes()}
                                                             size={this.currentTypes().length} model='typeFilter'
                                                             multiple={true}/>
                                                <span className='help-block m-b-none'>
                                                    What event types to use for this achievement
                                                </span>
                                            </div>
                                        </FormGroup>

                                        <div className='hr-line-dashed'></div>

                                        <div className='col-sm-10 col-sm-offset-2'>
                                            <ButtonGroup actions={[EXPRESSION, SCRIPT]} active={this.state.ruleState}
                                                         onChange={this.toggleRule.bind(this)}/>
                                        </div>
                                        {this.getRuleInput()}

                                        <Condition
                                            show={AchievementTemplateType.templateEqual(
                                                    this.state.achievement.template,
                                                    AchievementTemplateType.counter) ||
                                                AchievementTemplateType.templateEqual(
                                                    this.state.achievement.template,
                                                    AchievementTemplateType.accumulator)
                                            }>
                                            <div className='hr-line-dashed'></div>

                                            <FormGroup className='form-group' model='countTarget'>
                                                <label className='col-sm-2 control-label'>Count Target</label>
                                                <div className='col-sm-10'>
                                                    <FormInput type='text' model='countTarget'
                                                               validators={[Validators.required(), Validators.number()]}
                                                               className='form-control'/>
                                                    <span className='help-block m-b-none'>
                                                    At what count/value is the trigger considered to be completed
                                                </span>
                                                </div>
                                            </FormGroup>
                                        </Condition>

                                    </div>
                                </div>
                            </div>
                        </div>

                        {this.getPresentationConfig()}
                        {this.getIntervalConfig()}
                        {this.getBonusActivation()}
                        {this.getItemAward()}

                        <div className='row'>
                            <div className='col-lg-12'>
                                <div className='ibox-content'>
                                    <div className='hr-line-dashed'></div>
                                    <FormSubmitError/>
                                    <FormSubmitSuccess text='Trigger was updated successfully'/>
                                    <div className='form-group'>
                                        <div className='col-sm-12'>
                                            <Submit className='ladda-button btn-lg btn-primary'>Save Trigger</Submit>
                                        </div>
                                    </div>
                                    <div className='hr-line-dashed'></div>
                                </div>
                            </div>
                        </div>

                    </Form>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/ui/view/achievements/TriggersConfigView.tsx